import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../components/button/button.component';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { filter, switchMap, take } from 'rxjs/operators';
import { AuthGuard } from '../services/auth.guard';
import { AuthService } from '../services/auth.service';
import { UsersManagementService } from '../services/usersManagement.service';
import { LanguageService } from '../services/language.service';
import { TenantsService } from '../services/tenants.service';
import { ThemeService } from '../services/theme.service';
import { extractTenantIdFromPath } from '../services/tenant-id.util';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
  selector: 'app-authentication',
  templateUrl: './authentication.component.html',
  styleUrls: ['./authentication.component.css']
})
export class AuthenticationComponent implements OnInit {
  private readonly loginBackgroundMapfre = '../../../../../assets/images/login_background_mapfre.svg';
  private readonly loginBackgroundAtos = '../../../../../assets/images/login_background_atos.svg';
  private readonly loginBackgroundDefault = '../../../../../assets/images/login_background.svg';

  variables: any;
  loginForm: any;

  userData: any;
  openDiv: boolean = false;

  mailErrorMessage = '';
  mailInvalid = false;

  passErrorMessage = '';
  paswordInvalid = false;

  errorDetected: boolean = false;
  errorText: string = "";

  isLoading: boolean = false;

  /** Tenant logo to render above the login form (null → fallback default). */
  tenantLogoUrl: string | null = null;
  
  /**
   * Global SSO entry URL read from {@code environment.ssoUrl}. When the
   * resolved tenant has {@code useSso=true} and this URL is set, the login
   * page renders only the "Sign in with SSO" button.
   */
  readonly ssoUrl: string = environment.ssoUrl || '';
  /**
   * {@code true} when the resolved tenant has chosen SSO as its login mode.
   * Drives the template: SSO button XOR user/password form.
   * Defaults to {@code false} (password mode) for the generic / pre-tenant route.
   */
  tenantUsesSso = false;
  /** {@code true} when the login URL is {@code /app/{tenantId}/...} (tenant already resolved). */
  isTenantPrefixedLogin = false;
  /** Avoids flashing the wrong login mode while tenant branding is loading from the API. */
  loginBrandingReady = false;

  /** Optional Atos SAML on generic login only (not on {@code /app/{tenant}} routes). */
  get showSamlAlternativeLogin(): boolean {
    return !this.isTenantPrefixedLogin;
  }

  /** Shows the user/password form on generic login, or on tenant login when SSO is disabled. */
  get showPasswordLoginForm(): boolean {
    return !this.isTenantPrefixedLogin || (this.loginBrandingReady && !this.tenantUsesSso);
  }

  /** Shows the SSO entry on generic login, or on tenant login when SSO is enabled. */
  get showSsoLoginButton(): boolean {
    return !this.isTenantPrefixedLogin || (this.loginBrandingReady && this.tenantUsesSso);
  }

  /** Chooses login background image according to the currently resolved branding. */
  get loginBackgroundImageUrl(): string {
    const brandingKey = this.resolveBrandingKey();

    if (brandingKey.includes('mapfre')) {
      return this.loginBackgroundMapfre;
    }

    if (brandingKey.includes('atos')) {
      return this.loginBackgroundAtos;
    }

    return this.loginBackgroundDefault;
  }

  constructor(
    public authGuard: AuthGuard,
    public authService: AuthService,
    private languageService: LanguageService,
    private usersManagementService: UsersManagementService,
    private tenantsService: TenantsService,
    private themeService: ThemeService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {

    this.loginForm = this.fb.group(
      {
        emailCtrl: new FormControl('', [Validators.required, Validators.email]),
        passwordCtrl: new FormControl('', Validators.required),
      }
    )
  }

  ngOnInit() {

    this.isTenantPrefixedLogin = extractTenantIdFromPath(window.location.pathname) !== null;
    if (this.isTenantPrefixedLogin) {
      this.themeService.tenantBrandingResolved$.pipe(
        filter(resolved => resolved),
        take(1)
      ).subscribe(() => {
        this.loginBrandingReady = true;
        this.cdr.detectChanges();
      });
    } else {
      this.loginBrandingReady = true;
    }

    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    this.themeService.branding$.subscribe(branding => {
      this.tenantLogoUrl = branding?.logo ?? null;
      this.tenantUsesSso = !!branding?.useSso;
    });

    // SAML ACS redirect: backend sends user back to /login?sso_token=... or ?sso_error=...
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      if (params['sso_token']) {
        const token = params['sso_token'];
        const name = params['name'] || '';
        const lastName = params['lastName'] || '';
        const language = params['language'] || 'es';

        this.languageService.language = language;
        sessionStorage.setItem('language', language);
        this.authService.setToken(token, name + ' ' + lastName, 14400000);

        // SSO sessions should never be redirected to the local password reset screen,
        // even if the backend includes forcePasswordChange in the callback query.
        this.redirectToTenantDashboard();
        return;
      }

      if (params['sso_error']) {
        this.errorDetected = true;
        this.errorText = this.resolveSsoErrorMessage(params['sso_error']);
        this.cdr.detectChanges();
      }
    });

    this.paswordInvalid = false;

    if (this.authGuard.isAuthenticated()) {
      this.redirectToTenantDashboard();
    }
  }

  /**
   * After authentication, fetch the caller's tenant, apply its branding so
   * colours / logo are visible immediately, and navigate to the target path
   * dropping any {@code /app/{id}/} prefix from the URL.
   *
   * The {@code /app/{id}/} prefix exists only to brand the unauthenticated
   * login screen via the public branding endpoint. Once the user is logged
   * in, branding comes from {@code /tenants/current}, so the URL stays clean
   * ({@code /dashboard}, {@code /users}, …) regardless of the path the user
   * arrived through. {@code win0dow.location.href} forces a full reload so
   * {@code APP_BASE_HREF} is recomputed back to {@code /}.
   */
  private redirectToTenantDashboard(targetPath: string = 'dashboard'): void {
    this.authService.userDataLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => this.tenantsService.loadCurrent())
    ).subscribe({
      next: tenant => {
        if (tenant) {
          this.themeService.applyBranding(tenant);
        }
        const onTenantPrefixedUrl = extractTenantIdFromPath(window.location.pathname) !== null;
        if (onTenantPrefixedUrl) {
          window.location.href = `/${targetPath}`;
        } else {
          this.router.navigate([targetPath]);
        }
      },
      error: () => this.router.navigate([targetPath])
    });
  }

  changeLanguage(lang: string) {
    this.languageService.language = lang;
    location.reload();
    sessionStorage.setItem('language', lang);
  }

  setSpanish() {
    this.languageService.language = 'es';
    location.reload();
    sessionStorage.setItem('language', 'es');
  }

  actionDiv(s: boolean) {
    this.openDiv = !this.openDiv;
  }


  get passwordCtrl() {
    return this.loginForm.get('passwordCtrl');
  }

  get emailCtrl() {
    return this.loginForm.get('emailCtrl');
  }


  shouldShowPasswordError() {
    const ctrl = this.passwordCtrl;
    if (ctrl?.hasError('required') && (ctrl.dirty || ctrl.touched)) {
      this.paswordInvalid = true;
      this.passErrorMessage = this.variables.passRequired
    }
    if (ctrl?.hasError('minlength') && (ctrl.dirty || ctrl.touched)) {
      this.paswordInvalid = true;
      this.passErrorMessage = this.variables.passRequired
    }
    return null;
  }

  cleanErrors() {
    this.passErrorMessage = ''
    this.paswordInvalid = false;
  }

  /** Redirects to the tenant SSO entry ({@code environment.ssoUrl}) or Atos SAML as fallback. */
  signInWithSso(): void {
    if (this.ssoUrl) {
      window.location.href = this.ssoUrl;
    } else {
      this.samlAuth();
    }
  }

  submitLogin() {
    const formGroup = this.loginForm.value;

    this.cleanErrors();

    const emailControl = this.loginForm.get('emailCtrl');
    const passwordControl = this.loginForm.get('passwordCtrl');

    if (emailControl?.invalid) {
      if (emailControl.errors?.['required']) {
        this.mailInvalid = true;
        this.mailErrorMessage = this.variables.mailRequired;
      } else if (emailControl.errors?.['email']) {
        this.mailInvalid = true;
        this.mailErrorMessage = this.variables.invalidEmailFormat;
      }
    } else {
      this.mailInvalid = false;
      this.mailErrorMessage = '';
    }

    if (passwordControl?.invalid) {
      if (passwordControl.errors?.['required']) {
        this.paswordInvalid = true;
        this.passErrorMessage = this.variables.passRequired;
      }
    } else {
      this.paswordInvalid = false;
      this.passErrorMessage = '';
    }

    if (this.loginForm.valid) {

      this.isLoading = true;

      this.authGuard.sendLoginData(this.loginForm.value.emailCtrl, this.loginForm.value.passwordCtrl).subscribe(
        (response: any) => {

          this.errorDetected = false;
          this.paswordInvalid = false;

          this.languageService.language = response.language ?? 'es';
          sessionStorage.setItem('language', this.languageService.language);

          this.authService.setToken(response.token, response.name + " " + response.lastName, 14400000);

          this.cdr.detectChanges();

          if (response.forcePasswordChange) {
            this.router.navigate(['reset-password']);
          } else if (response.token != null && response.token != undefined) {
            this.redirectToTenantDashboard();
          }

        },
        (error: any) => {
          console.error('Login failed', error);

          if (error.error.message === "Invalid credentials") {
            this.paswordInvalid = true;
            this.passErrorMessage = this.variables.credentialsIncorrect
          }
          else {
            this.errorDetected = true
            this.errorText = this.variables.detectedError
          }

          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
    }
  }

  /** Atos SAML: redirect to db-manager SAML login endpoint. */
  samlAuth() {
    window.location.href = environment.authUrl + '/saml/login';
  }

  /**
   * Maps the sso_error query param returned by the backend to a translated message.
   * Falls back to a generic message if the key is missing from the i18n bundle.
   */
  private resolveSsoErrorMessage(errorCode: string): string {
    const v = this.variables;
    switch (errorCode) {
      case 'user_inactive':
        return v?.ssoErrorUserInactive || 'Your user account is disabled. Please contact the administrator.';
      case 'org_not_allowed':
        return v?.ssoErrorOrgNotAllowed || 'Your organization is not registered in the system. Please contact the administrator.';
      case 'too_many_attempts':
        return v?.ssoErrorTooManyAttempts || 'Too many login attempts. Please wait a few minutes and try again.';
      case 'invalid_saml_response':
        return v?.ssoErrorInvalidResponse || 'The SSO response was invalid. Please try again.';
      case 'saml_parse_error':
        return v?.ssoErrorParseError || 'Error processing the SSO response. Please try again.';
      default:
        return v?.ssoError || 'SSO authentication failed. Please try again.';
    }
  }

  /** Returns a lower-cased token that identifies the active branding. */
  private resolveBrandingKey(): string {
    const tenantId = (this.themeService.currentTenantId || environment.defaultTenantId || '').trim().toLowerCase();
    if (tenantId) {
      return tenantId;
    }

    return (this.tenantLogoUrl || '').trim().toLowerCase();
  }
}
